/**
 * Public AI Feature Profitability — top-of-funnel CFO/CTO lead magnet
 * ====================================================================
 * Free, no-signup. Paste your AI features (calls/mo, $/call, share of
 * ARR) and instantly see which ones pay for themselves.
 * Style matches the Savings Calculator + Bill Score look so the free
 * tools feel like one product, not five.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Bot, ArrowLeft, Sparkles, Mail, CheckCircle2, Loader2,
  TrendingUp, TrendingDown, AlertTriangle, Plus, Trash2, Beaker,
  Twitter, Linkedin, Link as LinkIcon, Calculator, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import FAQSection from '../components/FAQSection';
import { faqsByPage } from '../data/faqs';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const a = Math.abs(n); const sign = n < 0 ? '-' : '';
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}$${(a / 1_000).toFixed(1)}K`;
  return `${sign}$${a.toFixed(0)}`;
};

const VERDICT_META = {
  highly_profitable: { label: 'Pays for itself', color: 'emerald', icon: TrendingUp },
  profitable:        { label: 'Profitable',     color: 'emerald', icon: CheckCircle2 },
  break_even:        { label: 'Break-even',     color: 'amber',   icon: AlertTriangle },
  losing_money:      { label: 'Costs more than it earns', color: 'amber',  icon: TrendingDown },
  no_data:           { label: 'Not enough data', color: 'slate',  icon: AlertTriangle },
};

const STARTER_FEATURES = [
  { slug: 'summarise', name: 'Auto-summarise', model: 'gpt-5.2',
    monthly_calls: 6_200, avg_cost_per_call_usd: 2.10, usage_share_of_arr_pct: 1.5 },
  { slug: 'chat', name: 'Chat assistant', model: 'claude-sonnet-4.5',
    monthly_calls: 412_000, avg_cost_per_call_usd: 0.034, usage_share_of_arr_pct: 18 },
  { slug: 'search', name: 'Semantic search', model: 'embedding + pinecone',
    monthly_calls: 1_840_000, avg_cost_per_call_usd: 0.0008, usage_share_of_arr_pct: 26 },
];

// ───────────────────── Shareable result card ─────────────────────
const ShareCard = ({ summary, features }) => {
  const profitable = (features || []).filter(
    (f) => f.verdict === 'highly_profitable' || f.verdict === 'profitable' || f.verdict === 'break_even',
  ).length;
  const losing = summary.losing_money_count || 0;
  const annualBleed = (features || [])
    .filter((f) => f.verdict === 'losing_money')
    .reduce((acc, f) => acc + Math.abs(f.monthly_margin_usd || 0) * 12, 0);

  const shareUrl = useMemo(() => {
    const u = new URL(window.location.origin + '/ai-profitability');
    u.searchParams.set('p', String(profitable));
    u.searchParams.set('l', String(losing));
    if (annualBleed > 0) u.searchParams.set('b', String(Math.round(annualBleed)));
    return u.toString();
  }, [profitable, losing, annualBleed]);

  const cardImageUrl = useMemo(() => {
    const u = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/ai-feature-profitability/share-card.svg`);
    u.searchParams.set('profitable', String(profitable));
    u.searchParams.set('losing', String(losing));
    u.searchParams.set('annual_bleed_usd', String(Math.round(annualBleed)));
    return u.toString();
  }, [profitable, losing, annualBleed]);

  const tweetText = losing > 0
    ? `I just audited my AI feature unit economics — ${losing} feature(s) costing more than they earn (~$${(annualBleed/1000).toFixed(0)}K/yr). Audit yours free in 2 minutes:`
    : `I just audited my AI feature unit economics — all ${profitable} feature(s) pay for themselves. Audit yours free in 2 minutes:`;
  const tweetHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied');
    } catch {
      toast.error('Copy failed — try a different browser');
    }
  };

  return (
    <Card className="bg-slate-900 text-slate-100 border-white/10" data-testid="share-card-section">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Share2 className="h-5 w-5 text-emerald-400" /> Share your result
        </CardTitle>
        <CardDescription className="text-slate-400">
          Drop this card in Slack, LinkedIn or Twitter — every share is a free CFO conversation starter for your network.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <img
          src={cardImageUrl}
          alt="AI Feature Profitability share card"
          className="w-full max-w-2xl rounded-lg border border-white/10"
          data-testid="share-card-image"
        />
        <div className="flex flex-wrap gap-2">
          <Button onClick={copy} size="sm" variant="outline"
            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" data-testid="share-copy-btn">
            <LinkIcon className="h-4 w-4 mr-2" /> Copy share link
          </Button>
          <a href={tweetHref} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800" data-testid="share-twitter-btn">
              <Twitter className="h-4 w-4 mr-2" /> Tweet
            </Button>
          </a>
          <a href={linkedinHref} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800" data-testid="share-linkedin-btn">
              <Linkedin className="h-4 w-4 mr-2" /> LinkedIn
            </Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const PublicAIProfitability = () => {
  const [searchParams] = useSearchParams();
  const [arr, setArr] = useState(3_600_000);
  const [features, setFeatures] = useState(STARTER_FEATURES);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // OG image: if the URL has ?p=&l=&b= (someone shared their result),
  // build a tailored share-card.svg URL. Otherwise fall back to the
  // default sample card. Crawlers (Twitter/LinkedIn/Slack) hit the
  // page once and grab whatever's in the meta tag at SSR/CSR time.
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const ogImageUrl = useMemo(() => {
    const p = searchParams.get('p');
    const l = searchParams.get('l');
    const b = searchParams.get('b');
    const params = new URLSearchParams();
    if (p) params.set('profitable', p);
    if (l) params.set('losing', l);
    if (b) params.set('annual_bleed_usd', b);
    // Default fallback values keep the card looking sharp on first-visit.
    if (!params.toString()) {
      params.set('profitable', '3');
      params.set('losing', '1');
      params.set('annual_bleed_usd', '102000');
    }
    return `${apiBase}/api/ai-feature-profitability/share-card.svg?${params.toString()}`;
  }, [searchParams, apiBase]);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDone, setEmailDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eEmail, setEEmail] = useState('');
  const [eName, setEName] = useState('');
  const [eCompany, setECompany] = useState('');

  const compute = useMemo(
    () => async () => {
      if (!features.length) return;
      setLoading(true);
      try {
        const { data } = await axios.post(`${API_URL}/ai-feature-profitability/public-analyze`, {
          total_arr_usd: Number(arr) || 0,
          features: features.map((f) => ({
            slug: f.slug, name: f.name, model: f.model,
            monthly_calls: Number(f.monthly_calls) || 0,
            avg_cost_per_call_usd: Number(f.avg_cost_per_call_usd) || 0,
            usage_share_of_arr_pct: Number(f.usage_share_of_arr_pct) || 0,
          })),
        });
        setResult(data);
      } catch (e) {
        toast.error("Couldn't compute profitability", {
          description: e?.response?.data?.detail || e?.message,
        });
      } finally { setLoading(false); }
    }, [arr, features],
  );

  useEffect(() => {
    const t = setTimeout(() => { compute(); }, 350);
    return () => clearTimeout(t);
  }, [compute]);

  const updateFeature = (idx, patch) =>
    setFeatures((arr) => arr.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeFeature = (idx) => setFeatures((arr) => arr.filter((_, i) => i !== idx));
  const addFeature = () =>
    setFeatures((arr) => [...arr, {
      slug: `feature_${arr.length + 1}`, name: 'New feature', model: '',
      monthly_calls: 1000, avg_cost_per_call_usd: 0.01, usage_share_of_arr_pct: 5,
    }]);

  const submitEmail = async () => {
    if (!eEmail || !eEmail.includes('@')) {
      toast.error('Please enter a valid email'); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/ai-feature-profitability/waitlist`, {
        email: eEmail.trim(),
        name: eName.trim() || undefined,
        company: eCompany.trim() || undefined,
        losing_money_count: result?.summary?.losing_money_count || 0,
        total_monthly_cost_usd: result?.summary?.total_monthly_cost_usd || 0,
      });
      setEmailDone(true);
      toast.success(r.data?.message || 'Welcome — we\'ll be in touch shortly.');
    } catch (e) {
      toast.error('Could not save your details', {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally { setSubmitting(false); }
  };

  const summary = result?.summary;
  const sortedFeatures = result?.features || [];
  const worstFeature = sortedFeatures[0];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="public-ai-profitability">
      <SEOHead
        title="AI Feature Profitability — Free Audit"
        description="See if your AI features are paying for themselves. Free 2-minute audit — paste your features (calls/mo, $/call, % of ARR) and instantly see which ones earn their keep."
        canonical="https://cartieai.com/ai-profitability"
        ogImage={ogImageUrl}
        keywords="AI feature profitability, LLM cost, AI unit economics, AI margin, FinOps for AI"
      />
      {/* Top nav (matches Savings Calculator + Bill Score) */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 text-slate-100 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800" data-testid="back-link">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Hero */}
          <header>
            <Badge className="mb-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 tracking-wider">
              <Calculator className="w-3 h-3 mr-1" /> FREE TOOL · NO SIGN-UP
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-3">
              Is your{' '}
              <span className="text-emerald-600">
                AI feature
              </span>{' '}
              paying for itself?
            </h1>
            <p className="text-slate-600 max-w-3xl text-base sm:text-lg leading-relaxed">
              Every B2B SaaS in 2026 ships 3-8 distinct AI features. Each has wildly different unit economics — yet most founders can't answer the simplest question their CFO will ask in the next board meeting:{' '}
              <span className="text-slate-900 font-medium">"Is our <code className="text-emerald-300">summarise</code> feature paying for itself?"</span>
            </p>
            <p className="text-slate-400 text-sm mt-3">
              Edit the table below → see the per-feature P&amp;L. The math is shown — nothing hidden.
            </p>
          </header>

          {/* Inputs grid */}
          <div className="grid lg:grid-cols-[1fr,1.4fr] gap-6">
            {/* ARR */}
            <Card className="bg-slate-900 text-slate-100 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Bot className="h-5 w-5 text-emerald-400" /> Your annual revenue
                </CardTitle>
                <CardDescription className="text-slate-400">Used to attribute revenue to each feature based on the share you set per row.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">
                    ARR (USD / year)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">$</span>
                    <Input
                      type="number" min="0" step="10000" value={arr}
                      onChange={(e) => setArr(e.target.value)}
                      className="pl-7 bg-slate-950 border-slate-700 text-white text-xl font-semibold"
                      data-testid="arr-input"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-2 font-mono">≈ {fmtMoney(arr / 12)} / month</p>
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="bg-slate-900 text-slate-100 border-white/10">
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-emerald-400" /> Your AI features
                  </CardTitle>
                  <CardDescription className="text-slate-400">Add one row per distinct feature. Math updates live.</CardDescription>
                </div>
                <Button onClick={addFeature} size="sm" variant="outline"
                  className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                  data-testid="add-feature-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] text-slate-400 uppercase tracking-wider">
                    <tr>
                      <th className="p-2">Feature</th>
                      <th className="p-2">Model</th>
                      <th className="p-2 text-right">Calls/mo</th>
                      <th className="p-2 text-right">$/call</th>
                      <th className="p-2 text-right">% of ARR</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {features.map((f, i) => (
                      <tr key={i} data-testid={`feature-input-row-${i}`}>
                        <td className="p-1.5">
                          <Input value={f.name} onChange={(e) => updateFeature(i, { name: e.target.value })}
                            className="bg-slate-950 border-slate-700 text-white h-8 text-sm" data-testid={`f-name-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input value={f.model || ''} onChange={(e) => updateFeature(i, { model: e.target.value })}
                            className="bg-slate-950 border-slate-700 text-slate-300 h-8 text-xs font-mono" placeholder="e.g. gpt-5.2"
                            data-testid={`f-model-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input type="number" min="0" value={f.monthly_calls}
                            onChange={(e) => updateFeature(i, { monthly_calls: e.target.value })}
                            className="bg-slate-950 border-slate-700 text-white h-8 text-right font-mono text-sm"
                            data-testid={`f-calls-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input type="number" min="0" step="0.0001" value={f.avg_cost_per_call_usd}
                            onChange={(e) => updateFeature(i, { avg_cost_per_call_usd: e.target.value })}
                            className="bg-slate-950 border-slate-700 text-white h-8 text-right font-mono text-sm"
                            data-testid={`f-cost-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input type="number" min="0" max="100" step="0.5" value={f.usage_share_of_arr_pct}
                            onChange={(e) => updateFeature(i, { usage_share_of_arr_pct: e.target.value })}
                            className="bg-slate-950 border-slate-700 text-white h-8 text-right font-mono text-sm"
                            data-testid={`f-share-${i}`} />
                        </td>
                        <td className="p-1.5 text-right">
                          <Button onClick={() => removeFeature(i)} size="sm" variant="ghost"
                            className="text-slate-400 hover:text-amber-400 h-8 w-8 p-0" data-testid={`f-remove-${i}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          {/* Result KPIs */}
          {summary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="result-kpis">
                <Card className="bg-slate-900 text-slate-100 border-white/10"><CardContent className="p-4">
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Total monthly AI cost</p>
                  <p className="text-2xl font-bold font-mono text-white">{fmtMoney(summary.total_monthly_cost_usd)}</p>
                </CardContent></Card>
                <Card className="bg-slate-900 text-slate-100 border-white/10"><CardContent className="p-4">
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Net monthly margin</p>
                  <p className={`text-2xl font-bold font-mono ${summary.total_monthly_margin_usd < 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {fmtMoney(summary.total_monthly_margin_usd)}
                  </p>
                </CardContent></Card>
                <Card className="bg-slate-900 text-slate-100 border-white/10"><CardContent className="p-4">
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Features tracked</p>
                  <p className="text-2xl font-bold font-mono text-white">{summary.feature_count}</p>
                </CardContent></Card>
                <Card className={`border-white/10 ${summary.losing_money_count > 0 ? 'bg-amber-500/5 border-amber-500/30' : 'bg-slate-900'}`}>
                  <CardContent className="p-4">
                    <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Costs more than earns</p>
                    <p className={`text-2xl font-bold font-mono ${summary.losing_money_count > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {summary.losing_money_count}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Worst-feature callout — warm not threatening */}
              {worstFeature && worstFeature.verdict === 'losing_money' && (
                <Card className="bg-slate-900 border-amber-500/30 text-slate-100" data-testid="worst-feature-callout">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-white">
                          Heads up — your <code className="text-emerald-300">{worstFeature.slug}</code> feature is costing about {fmtMoney(Math.abs(worstFeature.monthly_margin_usd))}/month more than it earns.
                        </p>
                        <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
                          That works out to about <span className="text-white font-medium">{fmtMoney(Math.abs(worstFeature.monthly_margin_usd) * 12)} a year</span>. Common quick wins: cache aggressive responses, switch to a cheaper model for low-stakes calls, gate it behind a paid tier, or raise its usage-share-of-ARR if customers genuinely value it.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sorted feature list */}
              <Card className="bg-slate-900 text-slate-100 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-base">Per-feature P&amp;L · sorted by margin</CardTitle>
                  <CardDescription className="text-slate-400">Worst margin at the top — that's where the leverage is.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="sorted-feature-list">
                    {sortedFeatures.map((f) => {
                      const v = VERDICT_META[f.verdict] || VERDICT_META.no_data;
                      const Icon = v.icon;
                      const colorClass = (
                        v.color === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/5' :
                        v.color === 'amber'   ? 'border-amber-500/30 bg-amber-500/5' :
                        'border-white/10'
                      );
                      const accent = (
                        v.color === 'emerald' ? 'text-emerald-400' :
                        v.color === 'amber'   ? 'text-amber-400' :
                        'text-slate-400'
                      );
                      return (
                        <div key={f.slug} className={`p-4 rounded-md border ${colorClass}`} data-testid={`result-row-${f.slug}`}>
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto,auto,auto] items-center gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <p className="font-semibold truncate text-white">{f.name}</p>
                                <Badge variant="outline" className={`text-[11px] ${accent} border-current/40 bg-current/10`}>
                                  <Icon className="h-3 w-3 mr-1" />{v.label}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-400 truncate font-mono">
                                {f.slug} · {f.model || '—'} · {Number(f.monthly_calls).toLocaleString()} calls/mo
                              </p>
                            </div>
                            <div className="text-right text-xs">
                              <p className="text-slate-400">Cost</p>
                              <p className="font-mono font-bold text-slate-300">{fmtMoney(f.monthly_cost_usd)}</p>
                            </div>
                            <div className="text-right text-xs">
                              <p className="text-slate-400">Revenue</p>
                              <p className="font-mono font-bold text-emerald-400">{fmtMoney(f.monthly_revenue_share_usd)}</p>
                            </div>
                            <div className="text-right text-xs">
                              <p className="text-slate-400">Margin</p>
                              <p className={`font-mono font-bold ${accent}`}>{fmtMoney(f.monthly_margin_usd)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* CTA — warm, on-brand emerald */}
          <Card className="bg-slate-900 border-emerald-500/30 text-slate-100">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-1">Want a personalised plan?</h3>
                <p className="text-sm text-slate-400">
                  We'll send you the 3 highest-leverage savings for your AI features within 24 hours — written by a human, not a bot. Free, no commitment.
                </p>
              </div>
              <Button onClick={() => setEmailOpen(true)} size="lg"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                data-testid="request-plan-btn">
                <Mail className="h-4 w-4 mr-2" /> Request my plan
              </Button>
            </CardContent>
          </Card>

          {/* Share section */}
          {summary && <ShareCard summary={summary} features={sortedFeatures} />}

          {/* The math */}
          <Card className="bg-slate-900 text-slate-100 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Beaker className="h-5 w-5 text-emerald-400" /> The math (transparent on purpose)
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2 text-slate-400">
              <p><span className="text-white font-semibold">Cost</span> = <code className="text-emerald-300">avg_cost_per_call × monthly_calls</code>. Editable per row; nothing hidden.</p>
              <p><span className="text-white font-semibold">Revenue share</span> = <code className="text-emerald-300">(ARR / 12) × usage_share_of_arr_pct</code>. Your honest estimate of how much subscription revenue this feature drives. Use heuristics (engagement %, time-spent, perceived value); refine as you learn.</p>
              <p><span className="text-white font-semibold">Verdict</span>: ROI ≥ 200% pays for itself, ≥ 25% profitable, ±25% break-even, otherwise costs more than it earns.</p>
              <p className="text-xs italic pt-2 border-t border-white/5 text-slate-400">
                Why we built this: every AI SaaS founder we talk to has the same blind spot — they ship 4-8 AI features and have zero per-feature unit economics. Datadog has APM-level traces for this but it's an enterprise sale. We give you 80% of the answer in 5 minutes, free.
              </p>
              <div className="pt-3 flex gap-2 flex-wrap">
                <Link to="/customer-pl"><Button size="sm" variant="outline" className="border-slate-700 text-slate-400">Customer P&amp;L →</Button></Link>
                <Link to="/savings-calculator"><Button size="sm" variant="outline" className="border-slate-700 text-slate-400">Savings Calculator →</Button></Link>
                <Link to="/pr-cost-predictor"><Button size="sm" variant="outline" className="border-slate-700 text-slate-400">PR-time Cost →</Button></Link>
              </div>
            </CardContent>
          </Card>

          {loading && (
            <div className="text-center text-xs text-slate-400" data-testid="loading-indicator">
              <Loader2 className="h-3 w-3 inline animate-spin mr-1" /> recomputing…
            </div>
          )}
        </div>
      </main>

      {/* Email capture dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-slate-100" data-testid="email-dialog">
          {!emailDone ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">Get your AI Feature Profitability plan</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Personalised, written by a human, sent within 24 hours.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Input placeholder="you@company.com" value={eEmail} onChange={(e) => setEEmail(e.target.value)}
                  className="bg-slate-950 border-slate-700" data-testid="email-input" />
                <Input placeholder="Your name (optional)" value={eName} onChange={(e) => setEName(e.target.value)}
                  className="bg-slate-950 border-slate-700" data-testid="name-input" />
                <Input placeholder="Company (optional)" value={eCompany} onChange={(e) => setECompany(e.target.value)}
                  className="bg-slate-950 border-slate-700" data-testid="company-input" />
              </div>
              <DialogFooter>
                <Button onClick={() => setEmailOpen(false)} variant="ghost" className="text-slate-400">Maybe later</Button>
                <Button onClick={submitEmail} disabled={submitting}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="submit-email-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send my plan'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-6 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
              <p className="text-lg font-bold text-slate-900">You're in.</p>
              <p className="text-sm text-slate-400">We'll send your plan within 24 hours.</p>
              <Button onClick={() => { setEmailOpen(false); setEmailDone(false); setEEmail(''); setEName(''); setECompany(''); }}
                variant="outline" className="border-slate-700 text-slate-600">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <section className="px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <FAQSection
            title="AI Profitability FAQ"
            items={faqsByPage.ai_profitability}
            testIdPrefix="ai-profitability-faq"
            variant="card"
          />
        </div>
      </section>
    </div>
  );
};

export default PublicAIProfitability;
